import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Clock, MapPin, Calendar, AlertTriangle, Send, Target, CheckCircle2 } from 'lucide-react';
import Swal from 'sweetalert2';
import UseAxiosSecure from '../hooks/UseAxiosSecure';

const StaffDashboard = () => {
    const [isLocating, setIsLocating] = useState(false);
    const [isRegistering, setIsRegistering] = useState(false);
    const { register, handleSubmit, reset } = useForm();
    
    const axiosSecure = UseAxiosSecure();
    const queryClient = useQueryClient();

    // 0. FETCH MY HR PROFILE (To check if location is already registered!)
    const { data: myProfile, isLoading: profileLoading } = useQuery({
        queryKey: ['my-hr-profile'],
        queryFn: async () => {
            const res = await axiosSecure.get('/hr/me');
            return res.data?.data || null;
        }
    });

    // Check if coordinates exist in the database
    const hasRegisteredLocation = !!(myProfile?.registeredLat && myProfile?.registeredLng);

    // 1. REGISTER LOCATION MUTATION
    const registerLocationMutation = useMutation({
        mutationFn: async (locationData) => {
            const res = await axiosSecure.post('/hr/attendance/register-location', locationData);
            return res.data;
        },
        onSuccess: (data) => {
            // INSTANTLY update the UI to lock the button!
            queryClient.invalidateQueries(['my-hr-profile']);
            Swal.fire({ icon: 'success', title: 'Location Locked!', text: data.message, background: '#1a103c', color: '#fff' });
        },
        onError: (error) => {
            Swal.fire({ icon: 'error', title: 'Failed', text: error.response?.data?.message || 'Failed to set location', background: '#1a103c', color: '#fff' });
        }
    });

    // 2. CLOCK IN / OUT MUTATION
    const clockInOutMutation = useMutation({
        mutationFn: async (locationData) => {
            const res = await axiosSecure.post('/hr/attendance/toggle', locationData);
            return res.data;
        },
        onSuccess: (data) => {
            Swal.fire({ icon: 'success', title: 'Success!', text: data.message, background: '#1a103c', color: '#fff', timer: 2500, showConfirmButton: false });
        },
        onError: (error) => {
            Swal.fire({ icon: 'error', title: 'Punch Failed', text: error.response?.data?.message || 'Failed to update attendance', background: '#1a103c', color: '#fff' });
        }
    });

    // 3. GET GPS AND FIRE FUNCTION
    const getGPSAndExecute = (actionType) => {
        if (!navigator.geolocation) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Geolocation is not supported.', background: '#1a103c', color: '#fff' });
            return;
        }

        actionType === 'register' ? setIsRegistering(true) : setIsLocating(true);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const coords = { latitude: position.coords.latitude, longitude: position.coords.longitude };
                
                if (actionType === 'register') {
                    setIsRegistering(false);
                    registerLocationMutation.mutate(coords);
                } else {
                    setIsLocating(false);
                    clockInOutMutation.mutate({ employeeLat: coords.latitude, employeeLng: coords.longitude });
                }
            },
            (error) => {
                setIsRegistering(false);
                setIsLocating(false);
                Swal.fire({ icon: 'warning', title: 'Location Required', text: "Please allow GPS access.", background: '#1a103c', color: '#fff' });
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    };

    // LEAVE REQUEST MUTATION
    const leaveMutation = useMutation({
        mutationFn: async (data) => {
            const res = await axiosSecure.post('/hr/leave', data);
            return res.data;
        },
        onSuccess: () => {
            reset();
            Swal.fire({ icon: 'success', title: 'Request Sent', background: '#1a103c', color: '#fff', timer: 2000, showConfirmButton: false });
        }
    });

    if (profileLoading) return <div className="text-white text-center mt-20">Loading Workspace...</div>;

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-[#1a103c]/40 backdrop-blur-md p-6 rounded-3xl border border-white/5">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">👋 My Workspace</h2>
                <p className="text-slate-400 text-sm mt-1">Manage your shifts and request time off.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* --- TIME CLOCK CARD --- */}
                <div className="bg-[#1a103c]/60 backdrop-blur-xl border border-white/5 rounded-3xl p-8 shadow-2xl flex flex-col items-center text-center">
                    <div className="w-20 h-20 bg-cyan-500/10 rounded-full flex items-center justify-center mb-4 border border-cyan-500/20">
                        <Clock size={40} className="text-cyan-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Smart Time Clock</h3>
                    
                    {!hasRegisteredLocation && (
                        <p className="text-xs text-slate-400 mb-6 flex items-center gap-1 bg-black/20 p-2 rounded-lg">
                            <AlertTriangle size={14} className="text-orange-400"/> Step 1: You must register your location first.
                        </p>
                    )}

                    <div className={`w-full space-y-3 ${hasRegisteredLocation ? 'mt-6' : ''}`}>
                        
                        {/* THE LOCKED REGISTER BUTTON */}
                        <button 
                            onClick={() => getGPSAndExecute('register')}
                            disabled={hasRegisteredLocation || isRegistering || registerLocationMutation.isLoading}
                            className={`w-full py-3 border font-bold text-sm rounded-xl transition-all flex items-center justify-center gap-2 ${
                                hasRegisteredLocation 
                                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 cursor-not-allowed opacity-80' 
                                : 'bg-white/5 hover:bg-white/10 border-white/10 text-white active:scale-95'
                            }`}
                        >
                            {isRegistering ? (
                                <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                            ) : hasRegisteredLocation ? (
                                <CheckCircle2 size={16} />
                            ) : (
                                <Target size={16} />
                            )}
                            {hasRegisteredLocation ? 'Office Location Locked ✔️' : 'Register Office Location'}
                        </button>

                        {/* PUNCH BUTTON */}
                        <button 
                            onClick={() => getGPSAndExecute('punch')}
                            disabled={isLocating || clockInOutMutation.isLoading || !hasRegisteredLocation}
                            className="w-full py-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:opacity-50 text-white font-bold text-lg rounded-xl transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2"
                        >
                            {isLocating ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <MapPin size={20} />}
                            Punch In / Out
                        </button>
                    </div>
                </div>

                {/* --- LEAVE REQUEST CARD --- */}
                <div className="bg-[#1a103c]/60 backdrop-blur-xl border border-white/5 rounded-3xl p-8 shadow-2xl">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-orange-500/10 rounded-xl border border-orange-500/20">
                            <Calendar size={24} className="text-orange-400" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Request Time Off</h3>
                    </div>

                    <form onSubmit={handleSubmit((data) => leaveMutation.mutate(data))} className="space-y-4">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Date</label>
                            <input {...register("date", { required: true })} type="date" className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-orange-500" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Leave Type</label>
                            <select {...register("leaveType")} className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-orange-500">
                                <option value="Sick">Sick Leave</option>
                                <option value="Vacation">Paid Vacation</option>
                                <option value="Unpaid">Unpaid Leave</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Reason</label>
                            <textarea {...register("notes")} placeholder="Why do you need time off?" rows="2" className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 px-4 text-white resize-none focus:outline-none focus:border-orange-500"></textarea>
                        </div>
                        <button type="submit" disabled={leaveMutation.isLoading} className="w-full py-3 bg-orange-600/20 hover:bg-orange-600/40 text-orange-400 border border-orange-500/30 font-bold rounded-xl transition-all flex items-center justify-center gap-2 mt-2">
                            <Send size={18} /> Submit Request
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default StaffDashboard;